import { useState } from 'react';
import { MessageCircle, Loader2, Building2, UserRound, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { API_URL } from '../../config/api';
import { toast } from 'sonner';

export const ShareWhatsApp = ({ name, price, payLinks = [], plan }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    nombre: '', email: '', telefono: '', pais: '',
    prospecto_nombre: '', prospecto_telefono: '', prospecto_email: '', prospecto_pais: '',
  });

  const buildMessage = (agency, prospecto) => {
    const links = (payLinks || []).map((l) => `• ${l.provider}: ${l.url}`).join('\n');
    const who = agency || 'tu agencia Club Viajemos';
    const saludo = prospecto ? `¡Hola ${prospecto}! 👋` : '¡Hola! 👋';
    return `${saludo} Te comparto el acceso a la plataforma *${name}* de Club Viajemos (Travel Empire).\n\n`
      + `💳 Valor: US$${(price || 0).toLocaleString('en-US')} — pago único de por vida.\n`
      + `Puedes pagar de forma 100% segura por:\n${links}\n\n`
      + `📌 IMPORTANTE: Cuando completes tu pago, envíanos un correo a *info@clubviajemos.com* con la copia de tu comprobante e indícanos que tu agencia es *${who}*, para activar tu acceso.\n\n`
      + `Te acompaña: *${who}* 🌎✈️`;
  };

  const cleanPhone = (raw) => (raw || '').replace(/[^\d]/g, '');

  const handleShare = async () => {
    if (!form.nombre.trim()) { toast.error('Escribe el nombre de tu agencia'); return; }
    setSending(true);
    try {
      await fetch(`${API_URL}/api/agentes/referido`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: form.nombre, email: form.email, telefono: form.telefono, pais: form.pais, plan: name,
          prospecto_nombre: form.prospecto_nombre, prospecto_telefono: form.prospecto_telefono, prospecto_email: form.prospecto_email, prospecto_pais: form.prospecto_pais,
        }),
      });
    } catch (e) {
      console.error('referido save failed', e);
    }
    const msg = encodeURIComponent(buildMessage(form.nombre.trim(), form.prospecto_nombre.trim()));
    const phone = cleanPhone(form.prospecto_telefono);
    const waUrl = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    setSending(false);
    setOpen(false);
    toast.success(phone ? `¡Mensaje listo para ${form.prospecto_nombre.trim() || 'tu cliente'}!` : '¡Mensaje listo para compartir!');
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const fieldCls = "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#25D366]/40 focus-visible:border-[#25D366]";

  return (
    <>
      <button
        type="button"
        data-testid={`share-wa-${plan}`}
        onClick={() => setOpen(true)}
        className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-xl py-3 font-bold text-sm bg-[#25D366] text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0"
      >
        <MessageCircle className="h-4 w-4" /> Compartir por WhatsApp
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={`share-wa-modal-${plan}`}
          className="sm:max-w-md max-h-[92vh] overflow-y-auto p-0 gap-0 border-0 bg-white shadow-2xl rounded-2xl ring-1 ring-black/5"
        >
          {/* Header con marca WhatsApp */}
          <div className="relative bg-gradient-to-br from-[#25D366] to-[#128C7E] px-6 py-5 text-white">
            <DialogTitle className="flex items-center gap-2.5 text-white text-lg font-extrabold tracking-tight">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                <MessageCircle className="h-5 w-5" />
              </span>
              Compartir {name}
            </DialogTitle>
            <p className="mt-2 text-[13px] leading-snug text-white/90">
              Indica <strong className="font-bold">qué agencia</strong> registra y <strong className="font-bold">a quién</strong>. El link de pago se envía directo al WhatsApp de tu cliente.
            </p>
          </div>

          <div className="px-6 py-5 space-y-5 bg-white">
            {/* Sección agencia */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-[#128C7E]">
                <Building2 className="h-4 w-4" /> Tu agencia (quién registra)
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-slate-700">Nombre de tu agencia *</Label>
                <Input data-testid={`share-wa-nombre-${plan}`} className={fieldCls} value={form.nombre}
                  onChange={set('nombre')} placeholder="Ej: Viajes Global" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold text-slate-700">Email</Label>
                  <Input data-testid={`share-wa-email-${plan}`} className={fieldCls} value={form.email} onChange={set('email')} placeholder="correo@..." />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold text-slate-700">WhatsApp</Label>
                  <Input data-testid={`share-wa-telefono-${plan}`} className={fieldCls} value={form.telefono} onChange={set('telefono')} placeholder="+51 9..." />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-slate-700">País</Label>
                <Input data-testid={`share-wa-pais-${plan}`} className={fieldCls} value={form.pais} onChange={set('pais')} placeholder="Ej: Perú" />
              </div>
            </div>

            {/* Sección cliente */}
            <div className="space-y-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                <UserRound className="h-4 w-4" /> A quién vas a registrar (cliente)
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-slate-700">Nombre del cliente</Label>
                <Input data-testid={`share-wa-prospecto-nombre-${plan}`} className={fieldCls} value={form.prospecto_nombre}
                  onChange={set('prospecto_nombre')} placeholder="Ej: María Pérez" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold text-slate-700">WhatsApp del cliente</Label>
                  <Input data-testid={`share-wa-prospecto-telefono-${plan}`} className={fieldCls} value={form.prospecto_telefono}
                    onChange={set('prospecto_telefono')} placeholder="+1 305..." />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold text-slate-700">Email del cliente</Label>
                  <Input data-testid={`share-wa-prospecto-email-${plan}`} className={fieldCls} value={form.prospecto_email}
                    onChange={set('prospecto_email')} placeholder="cliente@..." />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-slate-700">País del cliente</Label>
                <Input data-testid={`share-wa-prospecto-pais-${plan}`} className={fieldCls} value={form.prospecto_pais}
                  onChange={set('prospecto_pais')} placeholder="Ej: Estados Unidos" />
              </div>
              <p className="text-[12px] leading-snug text-slate-500">
                Si agregas el WhatsApp del cliente, abriremos el chat directo con él. Si lo dejas vacío, podrás elegir el contacto al compartir.
              </p>
            </div>

            <Button data-testid={`share-wa-submit-${plan}`} onClick={handleShare} disabled={sending}
              className="w-full h-12 bg-[#25D366] hover:bg-[#1eb857] text-white font-bold text-[15px] rounded-xl shadow-lg transition-all hover:-translate-y-0.5">
              {sending ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <MessageCircle className="h-5 w-5 mr-2" />}
              Compartir ahora
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Tus datos se guardan de forma segura
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareWhatsApp;
